import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  Typography,
  Collapse,
  Chip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  People as CustomersIcon,
  LocalShipping as SupplierIcon,
  // Security as SecurityIcon,
  Newspaper as SchemeCardIcon,
  // PendingActions as RoznamaIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
  Assessment,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useReactDevTools } from "../context/ReactDevTools";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

const Sidebar = ({ isOpen = true, onToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDarkMode = theme.palette.mode === "dark";
  
  const { isSecretMode, handleSecretActivation, isMobile } = useReactDevTools();
  
  const isCollapsed = !isOpen;
  const [activeItem, setActiveItem] = useState(location.pathname);

  // Dropdown states
  const [customerOpen, setCustomerOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  
  // Click feedback state
  const [isClicking, setIsClicking] = useState(false);

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  const colors = {
    light: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      secondary: theme.palette.secondary.main,
      secondaryLight: theme.palette.secondary.light,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      background: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.85)'
        : 'rgba(30, 41, 59, 0.7)',
      border: 'rgba(37, 99, 235, 0.2)',
      chip: {
        background: 'rgba(37, 99, 235, 0.1)',
        border: 'rgba(37, 99, 235, 0.3)',
        text: '#2563EB'
      }
    },
    dark: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      secondary: theme.palette.secondary.main,
      secondaryLight: theme.palette.secondary.light,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      background: theme.palette.mode === 'dark'
        ? 'rgba(30, 41, 59, 0.7)'
        : 'rgba(255, 255, 255, 0.85)',
      border: 'rgba(148, 163, 184, 0.1)',
      chip: {
        background: 'rgba(96, 165, 250, 0.15)',
        border: 'rgba(96, 165, 250, 0.3)',
        text: '#60A5FA'
      }
    }
  };

  const currentColors = isDarkMode ? colors.dark : colors.light;

  // Handle Jewel Pro click for secret mode activation (Mobile only)
  const handleJewelProClick = () => {
    // Only allow on mobile
    if (!isMobile) return;
    
    // Visual feedback
    setIsClicking(true);
    setTimeout(() => setIsClicking(false), 500);
    
    // Trigger the activation
    handleSecretActivation();
  };

  const getMainMenuItems = () => {
    // Base items always visible
    const items = [
      {
        label: "Dashboard",
        icon: DashboardIcon,
        path: "/",
        badge: null,
      },
      {
        type: "dropdown",
        label: "Customers",
        icon: CustomersIcon,
        openState: customerOpen,
        setOpenState: setCustomerOpen,
        badge: "6",
        children: [
          { label: "View Customer", path: "/customers" },
          { label: "Add Customer Transaction", path: "/create-customer-transactions" },
          { label: "Generate Invoice", path: "/generate-invoice" },
          { label: "Customer Transaction History", path: "/customer-transactions" },
          { label: "Customer Ledger", path: "/customer-ledger" },
          { label: "Advance Payments", path: "/advance-payment" },
        ],
      },
      {
        type: "dropdown",
        label: "Suppliers",
        icon: SupplierIcon,
        openState: supplierOpen,
        setOpenState: setSupplierOpen,
        badge: `${!isSecretMode ? "1" : "4"}`,
        children: [
          { label: "View Supplier", path: "/supplier" },
          // These 3 items are hidden in secret mode
          ...(!isSecretMode ? [] : [
            { label: "Add Supplier Transaction", path: "/supplier-transaction" },
            { label: "Supplier Transaction History", path: "/supplier-transaction-history" },
            { label: "Supplier Ledger", path: "/supplier-ledger" },
          ]),
        ],
      },
      {
        type: "dropdown",
        label: "Stock",
        icon: Assessment,
        openState: stockOpen,
        setOpenState: setStockOpen,
        badge: "4",
        children: [
          { label: "Add Stock", path: "/addItem" },
          { label: "View Inventory", path: "/inventory" },
          { label: "Stock Transaction Report", path: "/StockTransactionReport" },
          { label: "Stock Verification", path: "/stock-verification" },
        ],
      },
    ];

    // Only add these items if NOT in secret mode
    if (isSecretMode) {
      items.push(
        // {
        //   label: "Roznama",
        //   icon: RoznamaIcon,
        //   path: "/roznama",
        //   badge: null,
        // },
        {
          label: "Scheme Card",
          icon: SchemeCardIcon,
          path: "/scheme-card",
          badge: null,
        },
        {
          label: "URD Purchase",
          icon: SupplierIcon,
          path: "/urd-purchase",
          badge: null,
        },
        // {
        //   label: "Add Security Deposits",
        //   icon: SecurityIcon,
        //   path: "/add-security-deposit",
        //   badge: null,
        // },
        // {
        //   label: "View Security Deposits",
        //   icon: SecurityIcon,
        //   path: "/security-deposits",
        //   badge: null,
        // }
      );
    }

    return items;
  };

  const isActive = (path) => {
    return activeItem === path;
  };

  const handleNavigate = (path) => {
    navigate(path);
    setActiveItem(path);
  };

  const handleToggleCollapse = () => {
    if (onToggle) {
      onToggle();
    }
  };

  const renderMenuItems = (items) => {
    return items.map((item, index) => {
      if (item.type === "dropdown") {
        const Icon = item.icon;
        const isActiveChild = item.children.some((child) => activeItem === child.path);

        return (
          <React.Fragment key={index}>
            <Tooltip
              title={isCollapsed ? item.label : ""}
              placement="right"
              arrow
            >
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => item.setOpenState(!item.openState)}
                  selected={isActiveChild}
                  sx={{
                    borderRadius: 2.5,
                    px: isCollapsed ? 1 : 1,
                    py: 1.5,
                    minHeight: 52,
                    position: "relative",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    backgroundColor: isActiveChild
                      ? isDarkMode
                        ? "rgba(96, 165, 250, 0.15)"
                        : "rgba(37, 99, 235, 0.1)"
                      : "transparent",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      borderRadius: 2.5,
                      padding: "2px",
                      background: isActiveChild
                        ? isDarkMode
                          ? `linear-gradient(135deg, ${currentColors.primaryLight} 0%, ${currentColors.primaryDark} 100%)`
                          : `linear-gradient(135deg, ${currentColors.primary} 0%, ${currentColors.primaryDark} 100%)`
                        : "transparent",
                      WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      opacity: isActiveChild ? 1 : 0,
                      transition: "opacity 0.3s ease",
                    },
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? "rgba(96, 165, 250, 0.08)"
                        : "rgba(37, 99, 235, 0.08)",
                      transform: "translateX(4px)",
                      "&::before": {
                        opacity: 0.5,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isCollapsed ? 0 : 44,
                      justifyContent: "center",
                      color: isActiveChild
                        ? currentColors.primary
                        : currentColors.textSecondary,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Icon sx={{ fontSize: 26 }} />
                  </ListItemIcon>

                  {!isCollapsed && (
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              fontWeight: isActiveChild ? 700 : 600,
                              fontSize: "0.95rem",
                              color: isActiveChild
                                ? currentColors.primary
                                : currentColors.textPrimary,
                              transition: "all 0.3s ease",
                            },
                          }}
                        />
                        {item.badge && (
                          <Chip
                            label={item.badge}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              backgroundColor: currentColors.chip.background,
                              color: isActiveChild
                                ? currentColors.primary
                                : currentColors.chip.text,
                              border: `1px solid ${currentColors.chip.border}`,
                              backdropFilter: "blur(10px)",
                              borderRadius: '8px',
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          transition: "transform 0.3s ease",
                          transform: item.openState ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 20,
                            color: isActiveChild
                              ? currentColors.primary
                              : currentColors.textSecondary,
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>

            {!isCollapsed && (
              <Collapse in={item.openState} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 1, pr: 1 }}>
                  {item.children.map((child) => {
                    const childActive = isActive(child.path);
                    return (
                      <ListItem key={child.path} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => handleNavigate(child.path)}
                          selected={childActive}
                          sx={{
                            borderRadius: 2,
                            px: 2,
                            py: 1.2,
                            minHeight: 42,
                            position: "relative",
                            transition: "all 0.25s ease",
                            backgroundColor: childActive
                              ? isDarkMode
                                ? "rgba(96, 165, 250, 0.15)"
                                : "rgba(37, 99, 235, 0.1)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: childActive
                                ? isDarkMode
                                  ? "rgba(96, 165, 250, 0.2)"
                                  : "rgba(37, 99, 235, 0.15)"
                                : isDarkMode
                                  ? "rgba(255, 255, 255, 0.04)"
                                  : "rgba(37, 99, 235, 0.05)",
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              justifyContent: "center",
                            }}
                          >
                            <CircleIcon
                              sx={{
                                fontSize: 8,
                                color: childActive
                                  ? currentColors.primary
                                  : currentColors.textSecondary,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: {
                                fontWeight: childActive ? 600 : 500,
                                fontSize: "0.875rem",
                                color: childActive
                                  ? currentColors.primary
                                  : currentColors.textSecondary,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      } else {
        const Icon = item.icon;
        const active = isActive(item.path);

        return (
          <Tooltip
            key={item.path}
            title={isCollapsed ? item.label : ""}
            placement="right"
            arrow
          >
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                selected={active}
                sx={{
                  borderRadius: 2.5,
                  px: isCollapsed ? 1 : 1,
                  py: 1.5,
                  minHeight: 52,
                  position: "relative",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  backgroundColor: active
                    ? isDarkMode
                      ? "rgba(96, 165, 250, 0.15)"
                      : "rgba(37, 99, 235, 0.1)"
                    : "transparent",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: 2.5,
                    padding: "2px",
                    background: active
                      ? isDarkMode
                        ? `linear-gradient(135deg, ${currentColors.primaryLight} 0%, ${currentColors.primaryDark} 100%)`
                        : `linear-gradient(135deg, ${currentColors.primary} 0%, ${currentColors.primaryDark} 100%)`
                      : "transparent",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    opacity: active ? 1 : 0,
                    transition: "opacity 0.3s ease",
                  },
                  "&:hover": {
                    backgroundColor: isDarkMode
                      ? "rgba(96, 165, 250, 0.08)"
                      : "rgba(37, 99, 235, 0.08)",
                    transform: "translateX(4px)",
                    "&::before": {
                      opacity: 0.5,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isCollapsed ? 0 : 44,
                    justifyContent: "center",
                    color: active
                      ? currentColors.primary
                      : currentColors.textSecondary,
                    transition: "all 0.3s ease",
                  }}
                >
                  <Icon sx={{ fontSize: 22 }} />
                </ListItemIcon>

                {!isCollapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: {
                        fontWeight: active ? 700 : 600,
                        fontSize: "0.95rem",
                        color: active
                          ? currentColors.primary
                          : currentColors.textPrimary,
                        transition: "all 0.3s ease",
                      },
                    }}
                  />
                )}

                {isCollapsed && active && (
                  <Box
                    sx={{
                      position: "absolute",
                      right: 12,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: currentColors.primary,
                      boxShadow: `0 0 8px ${currentColors.primary}40`,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </Tooltip>
        );
      }
    });
  };

  const mainMenuItems = getMainMenuItems();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          boxSizing: "border-box",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflowX: "hidden",
          backgroundColor: isDarkMode
            ? "rgba(30, 41, 59, 0.3)"
            : "rgba(255, 255, 255, 0.3)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRight: `1px solid ${currentColors.border}`,
          boxShadow: isDarkMode
            ? theme.shadows[4]
            : "0 8px 32px rgba(30, 41, 59, 0.15)",
          borderRadius: 1,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 1.5,
          borderBottom: `1px solid ${currentColors.border}`,
        }}
      >
        {!isCollapsed && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: currentColors.textPrimary,
              opacity: 0.8,
            }}
          >
            Navigation
          </Typography>
        )}
        <Tooltip title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
          <IconButton
            size="small"
            onClick={handleToggleCollapse}
            sx={{
              marginLeft: isCollapsed ? 0 : "auto",
              borderRadius: 2,
              backgroundColor: isDarkMode
                ? "rgba(30, 41, 59, 0.3)"
                : "rgba(255, 255, 255, 0.6)",
              backdropFilter: "blur(10px)",
              border: `1px solid ${currentColors.border}`,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              color: currentColors.textPrimary,
              "&:hover": {
                backgroundColor: isDarkMode
                  ? "rgba(30, 41, 59, 0.5)"
                  : "rgba(255, 255, 255, 0.8)",
                transform: "scale(1.1)",
                color: currentColors.primary,
              },
            }}
          >
            {isCollapsed ? (
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <List sx={{ flex: 1, pt: 2.5 }}>{renderMenuItems(mainMenuItems)}</List>

      {/* Footer with Jewel Pro */}
      {!isCollapsed && (
        <Box
          sx={{
            p: 2.5,
            borderTop: `1px solid ${currentColors.border}`,
            textAlign: "center",
            backgroundColor: isDarkMode
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(37, 99, 235, 0.03)",
            backdropFilter: "blur(10px)",
            // Only make clickable on mobile
            cursor: isMobile ? 'pointer' : 'default',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isClicking ? 'scale(0.95)' : 'scale(1)',
            position: 'relative',
            // Only add hover effects on mobile
            ...(isMobile && {
              '&:hover': {
                backgroundColor: isDarkMode
                  ? "rgba(96, 165, 250, 0.08)"
                  : "rgba(37, 99, 235, 0.08)",
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }),
          }}
          onClick={handleJewelProClick}
          onTouchStart={handleJewelProClick}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: "1rem",
              mb: 0.5,
              background: isDarkMode
                ? 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)'
                : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              transition: 'all 0.3s ease',
              letterSpacing: '0.5px',
              // On desktop, add a subtle tooltip-like indicator
              ...(!isMobile && {
                cursor: 'default',
                '&::after': {
                  content: '""',
                  display: 'none', // Hide any indicator on desktop
                },
              }),
            }}
          >
            Jewel Pro
          </Typography>
          
          <Typography
            variant="caption"
            sx={{
              color: currentColors.textSecondary,
              display: "block",
              fontSize: "0.7rem",
              fontWeight: 500,
              transition: 'all 0.3s ease',
            }}
          >
            Version 1.0.0
            {isSecretMode && (
              <Chip
                label="DEV"
                size="small"
                sx={{
                  ml: 1,
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  backgroundColor: currentColors.chip.background,
                  color: currentColors.chip.text,
                  border: `1px solid ${currentColors.chip.border}`,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.6 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
            )}
          </Typography>
          
          {isMobile && isSecretMode && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                fontSize: '0.6rem',
                color: '#10b981',
                opacity: 0.8,
                fontStyle: 'italic',
                fontWeight: 600,
              }}
            >
              🔓 Secret mode active
            </Typography>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;